import { memo, useEffect } from 'react';
import { MakeDepositResponse, useBoolean, useGetFullInfo, useUpdateInfo } from 'react-easyrocket';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Fields } from './components/Fields';
import { FormSubmitButton } from './components/FormSubmitButton';
import { useProcessHelloclever } from './hooks/useProcessHelloclever';
import { HELLOCLEVER_PAYMENT_METHOD } from './constants';
import { mergeProfileData } from './utils/mergeProfileData';
import { prepareDefaultFormState } from './utils/prepareDefaultFormState';
import { useSendForm } from './hooks/useSendForm';
import { getChangedProfileData } from './utils/compareDataFromProfile';
import { FormSender } from './components/FormSender';
import { modifyFormData } from './utils/modifyFormData';
import { useModalContext } from 'src/hooks/useModalContext';
import { prepareDataToProfile } from './utils/prepareDataToProfile';
import { ModalWrapper } from 'src/components/ModalWrapper';
import { removeDepositPrevPath } from 'src/modules/depositReturnPath/utils';
import classNames from 'classnames';
import { isFilledUp } from './utils/isFilledUp';
import { Spinner } from 'src/components/Spinner';
import { clearTransactionType, setWithdrawalInProgress } from 'src/utils/transactionTypeFromLS';

type ExtendedMakeDepositResponse = MakeDepositResponse & {
  paymentMethodName?: string;
  isWithdrawal?: boolean;
};

const FormFields = () => {
  const { t } = useTranslation();
  const { modalProps = {} as ExtendedMakeDepositResponse } =
    useModalContext<ExtendedMakeDepositResponse>();
  const {
    transaction,
    checkoutUrl,
    paymentMethodName,
    formFields = [],
    isWithdrawal
  } = modalProps as Partial<ExtendedMakeDepositResponse>;
  const processIfHelloclever = useProcessHelloclever();
  const { fullInfo, refetch: refetchFullInfo } = useGetFullInfo({ enabled: false });
  const [isShowModal, showModal, hideModal] = useBoolean(false);
  const hasCodeField = formFields.some((field) => field.name === 'dialingCode');

  const { modifiedData, sendForm } = useSendForm({
    methodName: transaction?.paymentMethod
  });

  const form = useForm({
    defaultValues: async () => {
      const { data: profileResponse } = await refetchFullInfo();
      return prepareDefaultFormState(formFields, profileResponse?.data);
    },
    mode: 'onChange'
  });

  const {
    handleSubmit,
    formState: { isValid, isSubmitted, defaultValues }
  } = form;

  useEffect(() => {
    if (!defaultValues || isShowModal) return;

    const hasAllData = isFilledUp(formFields, defaultValues);

    if (hasAllData && isValid && !isSubmitted) {
      handleSubmit(onSubmit)();
    }
    if (!isValid && !hasAllData) {
      showModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues, isValid]);

  const { mutateAsync: updateProfile } = useUpdateInfo();

  const onSubmit = async (data: Record<string, string>) => {
    if (!isValid) return;
    const dataToProfile = getChangedProfileData(prepareDataToProfile(data), fullInfo);

    if (isWithdrawal) {
      setWithdrawalInProgress();
    }
    const shouldUpdateProfile = Object.keys(dataToProfile).length > 0;

    if (shouldUpdateProfile) {
      await updateProfile(dataToProfile);
      hideModal();
    }

    if (transaction?.paymentMethod === HELLOCLEVER_PAYMENT_METHOD) {
      processIfHelloclever({
        data: data,
        transaction,
        checkoutUrl,
        paymentMethodName
      });
      return;
    }

    if (shouldUpdateProfile) {
      const { data: profileResponse } = await refetchFullInfo();
      const newData = {
        ...data,
        ...mergeProfileData(dataToProfile, profileResponse?.data, hasCodeField)
      };
      sendForm(modifyFormData({ data: newData, hasCodeField }));

      return;
    }

    sendForm(modifyFormData({ data, hasCodeField }));
  };

  const handleClose = () => {
    clearTransactionType();
    removeDepositPrevPath();
  };

  return (
    <>
      {!isShowModal && (
        <div className="absolute inset-0 z-[901] w-full h-full flex justify-center items-center">
          <Spinner />
        </div>
      )}
      <ModalWrapper onClose={handleClose} className={classNames(!isShowModal && 'hidden')}>
        <div className="px-[15px] py-[20px] w-full max-w-[900px] 412:min-w-[390px]">
          <FormProvider {...form}>
            <form className="relative flex flex-col gap-4 m-2.5" onSubmit={handleSubmit(onSubmit)}>
              <p className="text-2xl jsm:text-xl font-bold whitespace-normal text-center text-mg_text">
                {t('general.paymentDetails')}
              </p>
              <div className="w-full flex flex-col items-center gap-3">
                {formFields?.map((field) => (
                  <Fields field={field} key={field.name} />
                ))}
              </div>
              <FormSubmitButton />
            </form>
          </FormProvider>
          {modifiedData && <FormSender data={modifiedData} action={checkoutUrl} />}
        </div>
      </ModalWrapper>
    </>
  );
};

export default memo(FormFields);
